import unfoldLine from '../../ics/utils/unfold-line';
import unformatText from '../../ics/utils/unformat-text';
import { dateUtils } from '../../utils/dateUtils';

const NEW_LINE = /\r\n|\n|\r/;

const EVENT = 'VEVENT';
const EVENT_START = 'BEGIN';
const EVENT_END = 'END';
const START_DATE_TZID = 'START_DATE_TZID';
const START_DATE = 'DTSTART';
const END_DATE_TZID = 'END_DATE_TZID';
const END_DATE = 'DTEND';
const DESCRIPTION = 'DESCRIPTION';
const SUMMARY = 'SUMMARY';
const LOCATION = 'LOCATION';
const CATEGORIES = 'CATEGORIES';
const RRULE = 'RRULE';
const UID = 'UID';
const ALARM = 'VALARM';
const EXDATE = 'EXDATE';
const X_ATTACHED_FUEL_ENTITY = 'X-ATTACHED-FUEL-ENTITY';
const X_ATTACHED_FUEL_ENTITY_ID = 'X-ATTACHED-FUEL-ENTITY-ID';
const X_ATTACHED_FUEL_ENTITY_TYPE = 'X-ATTACHED-FUEL-ENTITY-TYPE';
const X_WR_TIMEZONE = 'X-WR-TIMEZONE';
const X_ATTACHED_FUEL_DISPLAY_CLASS = 'X-ATTACHED-FUEL-DISPLAY-CLASS';
const X_ATTACHED_FUEL_EVENT_FLAGS = 'X-ATTACHED-FUEL-EVENT-FLAGS';
const X_ATTACHED_FUEL_GRAPHICS_CONFIG = 'X-ATTACHED-FUEL-GRAPHICS-CONFIG';
const X_ATTACHED_FUEL_CUSTOM_ATTRIBUTES = 'X-ATTACHED-FUEL-CUSTOM-ATTRIBUTES';
const X_SUBTITLE = 'X-SUBTITLE';

const keyMap = {
  [START_DATE_TZID]: 'startTimezone',
  [START_DATE]: 'start',
  [END_DATE_TZID]: 'endTimezone',
  [END_DATE]: 'end',
  [DESCRIPTION]: 'description',
  [SUMMARY]: 'title',
  [LOCATION]: 'location',
  [CATEGORIES]: 'categories',
  [RRULE]: 'rrule',
  [UID]: 'id',
  [EXDATE]: 'exdate',
  [X_ATTACHED_FUEL_ENTITY]: 'attachedFuelEntityId',
  [X_ATTACHED_FUEL_ENTITY_ID]: 'attachedFuelEntityId',
  [X_ATTACHED_FUEL_ENTITY_TYPE]: 'attachedFuelEntityType',
  [X_WR_TIMEZONE]: 'timezone',
  [X_ATTACHED_FUEL_DISPLAY_CLASS]: 'attachedFuelDisplayClassName',
  [X_ATTACHED_FUEL_EVENT_FLAGS]: 'attchedFuelEventFlags',
  [X_ATTACHED_FUEL_GRAPHICS_CONFIG]: 'attachedFuelGraphicsConfig',
  [X_ATTACHED_FUEL_CUSTOM_ATTRIBUTES]: 'attachedFuelCustomAttributes',
  [X_SUBTITLE]: 'subTitle',
};

const clean = (string) => unescape(string).trim();

const cleanRRULE = (str) => {
  let rruleStr = unescape(str).trim();
  if (rruleStr[rruleStr.length - 1] === ';') {
    rruleStr = rruleStr.substring(0, rruleStr.length - 1);
    return rruleStr;
  }
  return rruleStr;
};

/**
 * Extracts "America/New_York" from DTSTART;TZID="America/New_York"
 * @param key eg. DTSTART;TZID="America/New_York"
 */
const getTimezoneFromICSValue = (keyParts: string[]) => {
  if (!keyParts || keyParts.length < 2) {
    return null;
  }
  const key = keyParts[1];
  const tzIndex = key.indexOf('TZID=');
  if (tzIndex > -1) {
    return key.substring(tzIndex + 5, key.length).replace(/"/g, '');
  }
  return null;
};

const convertDateLocalToTimeZoneSpecificDate = (date: string, timezone: string) => {
  let newDate = date;
  if (dateUtils.isUTC(date)) {
    return date;
  }
  newDate = dateUtils.convertDateStrLocalToTimeZoneSpecificDate(date, timezone);
  return newDate;
};

const icsToJson = (icsData) => {
  // console.log('icsToJson', { icsData });
  const array = [];
  let currentObj = {};
  let lastKey = '';
  let exdate: string[] = [];
  let timezone = null;

  const lines = icsData.split(NEW_LINE);

  let isAlarm = false;
  for (let i = 0, iLen = lines.length; i < iLen; ++i) {
    const isFolded = /^[ \t]+.*/.test(lines[i]);
    const line = isFolded ? lines[i] : unfoldLine(lines, i);
    const len = line.length;
    // console.log('icsToJson', { len, isFolded, i, line });

    if (isFolded) {
      continue;
    }

    const lineData = line.split(':');

    let key = lineData[0];
    let value = lineData[1];
    if (lineData.length > 2) {
      for (let j = 2; j < lineData.length; j++) {
        value += `:${lineData[j]}`;
      }
    }

    let keyParts = [];
    if (key.indexOf(';') !== -1) {
      keyParts = key.split(';');
      key = keyParts[0];
      // Maybe do something with that second part later
    }

    if (lineData.length < 2) {
      if (key.startsWith('FREQ=')) {
        currentObj[keyMap[RRULE]] = cleanRRULE(key);
        lastKey = keyMap[RRULE];
      } else if (key.startsWith(' ') && lastKey !== undefined && lastKey.length) {
        currentObj[lastKey] += clean(line.substr(1));
      }
      continue;
    } else {
      lastKey = keyMap[key];
    }
    switch (key) {
      case EVENT_START:
        if (value === EVENT) {
          currentObj = {};
        } else if (value === ALARM) {
          isAlarm = true;
        }
        break;
      case EVENT_END:
        isAlarm = false;
        if (value === EVENT) {
          array.push(currentObj);
          exdate = [];
        }
        break;
      case X_WR_TIMEZONE:
        currentObj[keyMap[X_WR_TIMEZONE]] = value;
        timezone = value;
        break;
      case START_DATE:
        // console.log('here ', { key, value, keyParts, line });
        const startTimezone = value.endsWith('Z') ? 'utc' : getTimezoneFromICSValue(keyParts);
        const toStartTimezone = startTimezone || timezone;
        currentObj[keyMap[START_DATE_TZID]] = toStartTimezone;
        currentObj[keyMap[START_DATE]] = convertDateLocalToTimeZoneSpecificDate(value, toStartTimezone);
        break;
      case END_DATE:
        const endTimezone = value.endsWith('Z') ? 'utc' : getTimezoneFromICSValue(keyParts);
        const toEndTimezone = endTimezone || timezone;
        currentObj[keyMap[END_DATE_TZID]] = toEndTimezone;
        currentObj[keyMap[END_DATE]] = convertDateLocalToTimeZoneSpecificDate(value, toEndTimezone);
        break;
      case DESCRIPTION:
        if (!isAlarm) currentObj[keyMap[DESCRIPTION]] = clean(unformatText(value));
        break;
      case SUMMARY:
        currentObj[keyMap[SUMMARY]] = clean(unformatText(value));
        break;
      case LOCATION:
        currentObj[keyMap[LOCATION]] = clean(unformatText(value));
        break;
      case X_SUBTITLE:
        currentObj[keyMap[X_SUBTITLE]] = clean(unformatText(value));
        break;
      case CATEGORIES:
        currentObj[keyMap[CATEGORIES]] = clean(value);
        break;
      case RRULE: {
        let rruleValue = cleanRRULE(value);
        if (rruleValue.startsWith('DTSTART')) {
          for (let j = i + 1; j < lines.length; j += 1) {
            if (/^[ \t]+.*/.test(lines[j])) continue;
            const nextLine = unfoldLine(lines, j);
            if (nextLine.startsWith('FREQ=')) {
              rruleValue = cleanRRULE(nextLine);
              i = j;
            }
            break;
          }
        }
        currentObj[keyMap[RRULE]] = rruleValue;
        break;
      }
      case UID:
        currentObj[keyMap[UID]] = clean(value);
        break;
      case EXDATE:
        if (!exdate.includes(value)) {
          exdate.push(convertDateLocalToTimeZoneSpecificDate(value, toStartTimezone));
          currentObj[keyMap[EXDATE]] = exdate;
        }
        break;
      case X_ATTACHED_FUEL_ENTITY:
      case X_ATTACHED_FUEL_ENTITY_ID: // backwards compatibility
        currentObj[keyMap[X_ATTACHED_FUEL_ENTITY]] = clean(unformatText(value));
        break;
      case X_ATTACHED_FUEL_ENTITY_TYPE:
        currentObj[keyMap[X_ATTACHED_FUEL_ENTITY_TYPE]] = clean(unformatText(value));
        break;
      case X_ATTACHED_FUEL_DISPLAY_CLASS:
        currentObj[keyMap[X_ATTACHED_FUEL_DISPLAY_CLASS]] = clean(unformatText(value));
        break;
      case X_ATTACHED_FUEL_EVENT_FLAGS:
        currentObj[keyMap[X_ATTACHED_FUEL_EVENT_FLAGS]] = clean(unformatText(value));
        break;
      case X_ATTACHED_FUEL_GRAPHICS_CONFIG:
        currentObj[keyMap[X_ATTACHED_FUEL_GRAPHICS_CONFIG]] = clean(unformatText(value));
        break;
      case X_ATTACHED_FUEL_CUSTOM_ATTRIBUTES:
        currentObj[keyMap[X_ATTACHED_FUEL_CUSTOM_ATTRIBUTES]] = clean(unformatText(value));
        break;
      default:
        continue;
    }
  }
  // console.log('icsToJson', { array });
  return array;
};

export default icsToJson;
